/**
 * Section: Main Blog
 * Handles load more functionality and tag filtering
 */

class BlogSection {
  constructor(section) {
    this.section = section;
    this.grid = section.querySelector('[data-blog-grid]');
    this.loadMoreBtn = section.querySelector('[data-load-more]');
    this.infiniteScrollEl = section.querySelector('[data-infinite-scroll]');
    this.filterButtons = section.querySelectorAll('[data-filter]');
    this.isLoading = false;
    
    this.init();
  }
  
  init() {
    if (this.loadMoreBtn) {
      this.loadMoreBtn.addEventListener('click', this.handleLoadMore.bind(this));
    }
    
    if (this.infiniteScrollEl) {
      this.setupInfiniteScroll();
    }
    
    if (this.filterButtons.length > 0) {
      this.filterButtons.forEach(btn => {
        btn.addEventListener('click', this.handleFilter.bind(this));
      });
    }
  }
  
  setupInfiniteScroll() {
    this.infiniteScrollObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isLoading) {
          const nextUrl = this.infiniteScrollEl.dataset.nextPage;
          if (nextUrl) {
            this.loadMoreArticles(nextUrl);
          }
        }
      });
    }, { rootMargin: '200px' });
    
    this.infiniteScrollObserver.observe(this.infiniteScrollEl);
  }
  
  async loadMoreArticles(nextUrl) {
    if (this.isLoading) return;
    this.isLoading = true;
    if (this.loadMoreBtn) {
      this.loadMoreBtn.classList.add('loading');
      this.loadMoreBtn.setAttribute('disabled', true);
    }
    
    try {
      const response = await fetch(nextUrl);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newGrid = doc.querySelector('[data-blog-grid]');
      const newLoadMore = doc.querySelector('[data-load-more]');
      const newInfiniteScroll = doc.querySelector('[data-infinite-scroll]');
      
      if (newGrid) {
        const newArticles = newGrid.querySelectorAll('.blog-card');
        newArticles.forEach((article, index) => {
          article.style.opacity = '0';
          article.style.transform = 'translateY(20px)';
          this.grid.appendChild(article);
          
          setTimeout(() => {
            article.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
            article.style.opacity = '1';
            article.style.transform = 'translateY(0)';
          }, index * 50);
        });
      }
      
      if (this.infiniteScrollEl) {
        if (newInfiniteScroll) {
          this.infiniteScrollEl.dataset.nextPage = newInfiniteScroll.dataset.nextPage;
          if (this.loadMoreBtn) {
            this.loadMoreBtn.dataset.nextPage = newInfiniteScroll.dataset.nextPage;
          }
        } else {
          this.infiniteScrollEl.style.display = 'none';
          if (this.infiniteScrollObserver) {
            this.infiniteScrollObserver.disconnect();
          }
        }
      }
    } catch (error) {
      console.error('Error loading more articles:', error);
    } finally {
      if (this.loadMoreBtn) {
        this.loadMoreBtn.classList.remove('loading');
        this.loadMoreBtn.removeAttribute('disabled');
      }
      this.isLoading = false;
    }
  }
  
  async handleLoadMore(e) {
    e.preventDefault();
    
    const button = e.currentTarget;
    const nextUrl = button.dataset.nextPage;
    
    if (!nextUrl || button.classList.contains('loading')) return;
    
    button.classList.add('loading');
    
    try {
      const response = await fetch(nextUrl);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newGrid = doc.querySelector('[data-blog-grid]');
      const newButton = doc.querySelector('[data-load-more]');
      
      if (newGrid) {
        const newArticles = newGrid.querySelectorAll('.blog-card');
        newArticles.forEach((article, index) => {
          article.style.opacity = '0';
          article.style.transform = 'translateY(20px)';
          this.grid.appendChild(article);
          
          setTimeout(() => {
            article.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
            article.style.opacity = '1';
            article.style.transform = 'translateY(0)';
          }, index * 50);
        });
      }
      
      if (newButton) {
        button.dataset.nextPage = newButton.dataset.nextPage;
      } else {
        button.style.display = 'none';
      }
    } catch (error) {
      console.error('Error loading more articles:', error);
    } finally {
      button.classList.remove('loading');
    }
  }
  
  handleFilter(e) {
    const button = e.currentTarget;
    const filter = button.dataset.filter;
    
    // Update active state
    this.filterButtons.forEach(btn => btn.classList.remove('main-blog__filter-btn--active'));
    button.classList.add('main-blog__filter-btn--active');
    
    // Filter articles
    const articles = this.grid.querySelectorAll('.blog-card');
    
    articles.forEach(article => {
      if (filter === 'all') {
        article.style.display = '';
        this.animateArticle(article);
      } else {
        const articleTags = article.dataset.tags || '';
        if (articleTags.includes(filter)) {
          article.style.display = '';
          this.animateArticle(article);
        } else {
          article.style.display = 'none';
        }
      }
    });
  }
  
  animateArticle(article) {
    article.style.opacity = '0';
    article.style.transform = 'translateY(10px)';
    
    requestAnimationFrame(() => {
      article.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      article.style.opacity = '1';
      article.style.transform = 'translateY(0)';
    });
  }
}

if (!customElements.get('blog-page-section')) {
  class BlogPageSection extends HTMLElement {
    connectedCallback() {
      if (!this.blogSection) {
        this.blogSection = new BlogSection(this);
      }
    }
  }
  customElements.define('blog-page-section', BlogPageSection);
}

